/** React Query key for the collections list. */
export const COLLECTIONS_QUERY_KEY = ['collections'];

/** React Query key for one collection's stored config. */
export const collectionConfigKey = collectionUuid => ['collectionConfig', collectionUuid];

const PROJECT_COLLECTIONS_KEY_PREFIX = 'projectCollections';

/** React Query key prefix matching every project's analyzed-collections list. */
export const PROJECT_COLLECTIONS_QUERY_KEY = [PROJECT_COLLECTIONS_KEY_PREFIX];

/**
 * React Query key for one project's analyzed-collections list — the "Analyzed
 * Collections" picker's query. Shared with that read site rather than spelled
 * out there, for the same reason as {@link collectionConfigKey}: a key that
 * drifts from the writes invalidating it fails silently.
 */
export const projectCollectionsKey = projectUuid => [...PROJECT_COLLECTIONS_QUERY_KEY, projectUuid];

// Query key prefixes that show a collection's detection results/boundary.
// Invalidated (not just refetched-on-interval) once a detection job finishes,
// so results and the detected-area boundary catch up without the user having
// to wait out their own refetch intervals or manually reload the page.
export const COLLECTION_RESULT_QUERY_KEYS = new Set([
	'collectionDetections',
	'collectionConfig',
	'collectionDetectedArea',
	'collectionHexagons',
	'collectionDsp',
	PROJECT_COLLECTIONS_KEY_PREFIX,
]);

/**
 * True when a query shows collection results/boundary data. Matching on the key
 * prefix alone covers every projection/resolution/filter-body suffix.
 *
 * Exported so consumers can also *observe* these queries using the same
 * definition that invalidates them — see {@link isCollectionResultQueryFor} for
 * the single-collection variant.
 */
export const isCollectionResultQuery = query => COLLECTION_RESULT_QUERY_KEYS.has(query.queryKey[0]);

/**
 * True when a query shows results for one *specific* collection. The uuid's
 * position varies by query (`['collectionConfig', uuid]` vs
 * `['collectionDetections', projectUuid, uuid, projection]`), so it's matched
 * anywhere in the key rather than at a fixed index.
 *
 * Narrower than {@link isCollectionResultQuery}: use this to observe the fetches
 * one collection's results are waiting on, so unrelated collection traffic
 * (including the shared `projectCollections` list) doesn't read as "still
 * loading".
 */
export const isCollectionResultQueryFor = collectionUuid => query =>
	isCollectionResultQuery(query) && query.queryKey.includes(collectionUuid);

/** Invalidates every query in {@link COLLECTION_RESULT_QUERY_KEYS} for the given client. */
export const invalidateCollectionResults = queryClient =>
	queryClient.invalidateQueries({ predicate: isCollectionResultQuery });

/**
 * Mutation scope shared by every write that PATCHes a collection's config —
 * object styling, collection filters, and detection pointers.
 *
 * All of them replace the whole `detection_objects` array, so each is a
 * read-modify-write of the same document. React Query runs mutations sharing a
 * scope id strictly one at a time, which stops two concurrent saves from being
 * built off the same pre-write state and clobbering each other.
 */
export const COLLECTION_CONFIG_SCOPE = collectionUuid => ({
	id: `collection-config-${collectionUuid}`,
});

/**
 * Invalidates the caches that hold a collection's stored config after a write.
 *
 * `collections` matters as much as `collectionConfig`: the Object Picker's edit
 * path reads an object's stored entry from the collections *list* cache and
 * re-sends it verbatim, so a stale list there would quietly revert styling and
 * detection pointers saved from the layers panel.
 */
export const invalidateCollectionConfig = (queryClient, collectionUuid) => {
	queryClient.invalidateQueries({ queryKey: collectionConfigKey(collectionUuid) });
	queryClient.invalidateQueries({ queryKey: COLLECTIONS_QUERY_KEY });
};

/**
 * Invalidates every cache that renders a collection's *name* after a rename.
 *
 * The name is stored once but read from three separate caches, each with its own
 * multi-minute staleTime, so invalidating the Object Picker's list alone leaves
 * the old name on screen elsewhere:
 *   - `collections`       — the Object Picker's list (via invalidateCollectionConfig)
 *   - `collectionConfig`  — the layers panel's Collection header (useCollectionData)
 *   - `projectCollections` — the "Analyzed Collections" toolbar picker
 *
 * The project list is invalidated by prefix: the uuid of the project a renamed
 * collection was analyzed in isn't known at the rename site, and a stale name in
 * another project's list is the same bug.
 */
export const invalidateCollectionName = (queryClient, collectionUuid) => {
	invalidateCollectionConfig(queryClient, collectionUuid);
	queryClient.invalidateQueries({ queryKey: PROJECT_COLLECTIONS_QUERY_KEY });
};

/**
 * Drops every cache entry belonging to a deleted collection.
 *
 * Removed rather than invalidated: the collection is gone server-side, so a
 * refetch would only 404 while the stale rows stayed on screen in the meantime.
 * `isCollectionResultQueryFor` matches the uuid anywhere in the key, which
 * covers the config, detections, boundary, hexagon and DSP queries in one pass
 * without touching another collection's cache (`projectCollections` keys carry
 * no collection uuid, so the shared lists are left to the invalidations below).
 *
 * The two lists that named it are then invalidated so it disappears from the
 * Object Picker and from the "Analyzed Collections" picker — both are mounted
 * while a delete happens, so they refetch immediately.
 */
export const removeDeletedCollection = (queryClient, collectionUuid) => {
	queryClient.removeQueries({ predicate: isCollectionResultQueryFor(collectionUuid) });
	queryClient.invalidateQueries({ queryKey: COLLECTIONS_QUERY_KEY });
	queryClient.invalidateQueries({ queryKey: PROJECT_COLLECTIONS_QUERY_KEY });
};
